import { Accordion } from '@shared/ui/accordion';
import { Input, PreloaderCircular } from '@shared/ui/kit';
import stylex from '@stylexjs/stylex';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState, useDeferredValue } from 'react';
import { Segment } from '../segment-header';
import { styles } from './styles';
import { useConfirm } from '@shared/hooks/useConfirm';
import { useFetchTicketQr } from '@pages/event/api/useFetchTicketQr';
import { SearchIcon, CrossIcon } from '@shared/assets/icons';
import { UnstyledButton } from '@shared/ui/unstyled-button';

import { ISegmentParticipants } from '@features/add-participants-modal';
import { Ticket, TicketsFilterInput } from '@pages/event/api/useFetchTicketSegment';
import { useFetchDeleteTicket } from '@features/api/use-delete-tickets.ts';
import { useToast } from '@shared/hooks/useToast';
import { SegmentsTree } from './segments-tree';
import { VirtualizedParticipantsList } from './ui/virtualized-participants-list';
import { SegmentParticipantRow } from './ui/segment-participant-row';
import { EventSettings } from '@features/api/use-get-settings.ts';

const VIRTUALIZATION_THRESHOLD = 50;

type SegmentWithTickets = ISegmentParticipants & {
  tickets?: Ticket[];
  countTicket: number;
};

interface ISegmentCardProps {
  segment: SegmentWithTickets;
  checkedUsers: { [key: number]: number };
  onDeleteSegment: (segment: ISegmentParticipants) => void;
  onAddSegment: (users: { userId: number; ticketId: number }[]) => void;
  toggleUser: (user: Ticket) => void;
  toggleAllUsers: (users: { userId: number; ticketId: number }[], isChecked: boolean, tickets?: Ticket[]) => void;
  onToggle: (isOpen: boolean) => void;
  setCheckedUsers: (
    users: { [key: number]: number } | ((prev: { [key: number]: number }) => { [key: number]: number })
  ) => void;
  onTicketDelete?: (ticketId: number) => void;
  ticketFilter?: TicketsFilterInput;
  addedTicketIds: number[];
  registerRefetch?: (segmentId: number, refetchFn: () => void) => void;
  isTreeView?: boolean;
  refetchSegmentsQuery: () => void;
  eventId?: number;
  refetchCategories?: () => void;
  eventDateEnd?: string;
  eventDateStart?: string;
  showAddForNoSegment?: boolean;
  eventSettings?: EventSettings | null;
}

export const SegmentCard: FC<ISegmentCardProps> = ({
  segment,
  onDeleteSegment,
  onAddSegment,
  checkedUsers,
  toggleUser,
  toggleAllUsers,
  onToggle,
  setCheckedUsers,
  onTicketDelete,
  ticketFilter,
  addedTicketIds,

  isTreeView,
  registerRefetch,
  refetchSegmentsQuery,
  eventId,
  refetchCategories,
  eventDateEnd,
  eventDateStart,
  showAddForNoSegment = false,
  eventSettings,
}) => {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const { openConfirmDialog } = useConfirm();
  const [currentTicketId, setCurrentTicketId] = useState(0);

  useEffect(() => {
    if (!addedTicketIds?.length || !segment.tickets?.length) return;
    setCheckedUsers((prev) => {
      const next = { ...prev };
      segment.tickets!.forEach((t) => {
        if (t.participant?.active && addedTicketIds.includes(t.participant.id)) next[t.id] = t.participant.id;
      });
      return next;
    });
  }, [addedTicketIds, segment.tickets, setCheckedUsers]);

  const { deleteTickets, fetching: isLoading } = useFetchDeleteTicket();
  const { showToast } = useToast();
  const handleDelete = useCallback(
    async (ticket: Ticket) => {
      const res = await openConfirmDialog(
        `Удалить участника ${ticket.participant?.firstName ?? ''} ${ticket.participant?.lastName ?? ''}?`
      );
      if (!res) return;

      try {
        const response = await deleteTickets({ ticketIds: [ticket.id] });
        if (response.data?.deleteTickets.success) {
          onTicketDelete?.(ticket.id);
          refetchSegmentsQuery();
          refetchCategories?.();
        } else {
          showToast('Ошибка при удалении участника', 'error');
        }
      } catch {
        showToast('Произошла ошибка при удалении участника', 'error');
      }
    },
    [openConfirmDialog, deleteTickets, onTicketDelete, refetchSegmentsQuery, refetchCategories, showToast]
  );

  const handleChangeSearch = useCallback((string: React.SetStateAction<string>) => {
    setSearch(string);
  }, []);

  const { data, fetching, reexecuteQuery } = useFetchTicketQr(currentTicketId);
  const downloadedTicketIdRef = useRef<number | null>(null);

  const getQrCodeFileName = useCallback(
    (userName: string, userSurname: string, ticketId: number) => `qr_code_${userName}_${userSurname}_${ticketId}.svg`,
    []
  );

  const handleGetTicketQR = useCallback(
    (ticketId: number) => {
      downloadedTicketIdRef.current = null;

      if (currentTicketId === ticketId) {
        reexecuteQuery({ requestPolicy: 'network-only' });
        return;
      }

      setCurrentTicketId(ticketId);
    },
    [currentTicketId, reexecuteQuery]
  );

  useEffect(() => {
    if (fetching || !data || !currentTicketId || downloadedTicketIdRef.current === currentTicketId) {
      return;
    }

    const userName = data.getTicketQrcodeById.firstName || '';
    const userSurname = data.getTicketQrcodeById.lastName || '';
    const blob = new Blob([data.getTicketQrcodeById.qr], { type: 'image/svg+xml' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = getQrCodeFileName(userName, userSurname, currentTicketId);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    downloadedTicketIdRef.current = currentTicketId;
  }, [data, fetching, currentTicketId, getQrCodeFileName]);

  const participants = segment.tickets ?? [];

  const filteredUsers = useMemo(() => {
    if (!deferredSearch.trim()) return participants;

    return participants.filter((item) => {
      const firstName = item.participant.firstName?.toLowerCase().trim();
      const lastName = item.participant.lastName?.toLowerCase().trim();
      const fullName = `${firstName} ${lastName}`;
      const reversedName = `${lastName} ${firstName}`;
      const searchLower = deferredSearch.toLowerCase().trim();

      return (
        fullName?.includes(searchLower) ||
        reversedName?.includes(searchLower) ||
        firstName?.includes(searchLower) ||
        lastName?.includes(searchLower)
      );
    });
  }, [deferredSearch, participants]);

  const participantCount = segment.countTicket;

  const { regularParticipants, freelanceParticipants } = useMemo(() => {
    const regular = filteredUsers.filter((ticket) => ticket.participant?.position !== null);
    const freelance = filteredUsers.filter((ticket) => ticket.participant?.position === null);
    return { regularParticipants: regular, freelanceParticipants: freelance };
  }, [filteredUsers]);

  const activeTickets = participants.filter((p) => p.participant?.active);
  const checked = activeTickets.length > 0 && activeTickets.every(({ id }) => !!checkedUsers[id]);

  const renderParticipantCard = useCallback(
    (ticket: Ticket, isFreelance = false) => (
      <SegmentParticipantRow
        ticket={ticket}
        isFreelance={isFreelance}
        checked={!!checkedUsers[ticket.id]}
        eventId={eventId}
        eventDateEnd={eventDateEnd}
        eventDateStart={eventDateStart}
        eventSettings={eventSettings}
        checkedUsers={checkedUsers}
        onToggle={toggleUser}
        onDelete={handleDelete}
        onGetTicketQR={handleGetTicketQR}
        refetchSegments={refetchSegmentsQuery}
        refetchCategories={refetchCategories}
      />
    ),
    [
      checkedUsers,
      toggleUser,
      handleDelete,
      handleGetTicketQR,
      eventId,
      refetchSegmentsQuery,
      refetchCategories,
      eventDateEnd,
      eventDateStart,
      eventSettings,
    ]
  );

  return (
    <Accordion
      onToggle={onToggle}
      key={segment.id}
      element={
        <Segment
          isIndeterminate={false}
          checked={!!participantCount && checked}
          title={segment.title}
          quantity={participantCount}
          onChange={() => {
            const users = [];
            for (const item of participants) {
              if (item.participant.active) {
                users.push({ userId: item.participant.id, ticketId: item.id });
              }
            }
            toggleAllUsers(users, !checked, participants);
          }}
          onAddParticipants={() => {
            onAddSegment(participants.map((t) => ({ userId: t.participant.id, ticketId: t.id })));
          }}
          onDelete={() => onDeleteSegment(segment)}
          showDeleteButton={!!segment.id}
          showAddButton={!!segment.id || showAddForNoSegment}
        />
      }
    >
      {isLoading ? (
        <div {...stylex.props(styles.loader)}>
          <PreloaderCircular variant="secondary" size="large" />
        </div>
      ) : (
        <>
          <div {...stylex.props(styles.input)}>
            <div {...stylex.props(styles.searchInputWrapper)}>
              <Input
                status="default"
                placeholder="Поиск"
                value={search}
                size="small"
                settings={{ width: '100%' }}
                changeFunction={handleChangeSearch}
              />
              <UnstyledButton onClick={() => handleChangeSearch('')}>
                {search ? (
                  <i {...stylex.props(styles.crossIcon)}>
                    <CrossIcon />
                  </i>
                ) : (
                  <i {...stylex.props(styles.searchIcon)}>
                    <SearchIcon />
                  </i>
                )}
              </UnstyledButton>
            </div>
          </div>

          {regularParticipants.length > 0 && (
            <>
              <div {...stylex.props(styles.navText)}>
                <p>Участники мероприятия</p>
              </div>
              {isTreeView ? (
                <SegmentsTree
                  toggleUser={toggleUser}
                  checkedUsers={checkedUsers}
                  setCheckedUsers={setCheckedUsers}
                  segment={segment}
                  search={deferredSearch}
                  registerRefetch={registerRefetch}
                  onDelete={handleDelete}
                  onGetQr={(id) => setCurrentTicketId(id)}
                  eventId={eventId}
                  refetchSegments={refetchSegmentsQuery}
                  refetchCategories={refetchCategories}
                  eventDateEnd={eventDateEnd}
                  eventDateStart={eventDateStart}
                  ticketFilter={ticketFilter}
                />
              ) : (
                <div {...stylex.props(styles.column)}>
                  {regularParticipants.length >= VIRTUALIZATION_THRESHOLD ? (
                    <VirtualizedParticipantsList
                      items={regularParticipants}
                      renderItem={renderParticipantCard}
                      scrollResetKey={deferredSearch}
                    />
                  ) : (
                    regularParticipants.map((ticket) => (
                      <React.Fragment key={ticket.id}>{renderParticipantCard(ticket)}</React.Fragment>
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {freelanceParticipants.length > 0 && (
            <>
              <div {...stylex.props(styles.navText)}>
                <p>Внештатные участники</p>
              </div>
              <div {...stylex.props(styles.column)}>
                {freelanceParticipants.map((ticket) => (
                  <React.Fragment key={ticket.id}>{renderParticipantCard(ticket, true)}</React.Fragment>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Accordion>
  );
};
